import { useDispatch, useSelector } from "react-redux";

import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { fetchContactsFromActionsPanelThunk } from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/effects";
import CRMContactsExportPanelContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/containers/CRMContactsExportPanel";
import { useContactsFlowStrategy } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import useActivePanelLifeCycle from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useActivePanelLifeCycle";
import {
    exportSelectedContactsToExcelForCompanyThunk,
    fetchTopNContactsFromCompanyAnalysisThunk,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import {
    selectActionsPanelFilters,
    selectNumberOfContactsSearchResults,
    selectSearchResultsAreFetching,
    selectSearchResultsWithRevealedInfo,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ExportToExcelFn } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/exportFlow";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

const ActivatedExportPanelContainer = (props: BaseActivatedPanelProps) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const dispatch = useDispatch();
    const contactsFlowStrategy = useContactsFlowStrategy();
    const actionsPanelFilters = useSelector(selectActionsPanelFilters);
    const totalCount = useSelector(selectNumberOfContactsSearchResults);
    const isLoading = useSelector(selectSearchResultsAreFetching);
    const { rows } = useSelector(selectSearchResultsWithRevealedInfo);

    const countries = [WORLDWIDE_COUNTRY_ID];

    useActivePanelLifeCycle();

    function refetchContacts() {
        void dispatch(fetchContactsFromActionsPanelThunk(params.key, actionsPanelFilters));
    }

    const handleExcelExportSubmit: ExportToExcelFn = async (topNOrPairs, isSilent) =>
        dispatch(
            exportSelectedContactsToExcelForCompanyThunk(
                topNOrPairs,
                params.key,
                contactsFlowStrategy.dataSource.exportExcel,
                isSilent,
            ),
        );

    async function fetchTopNContacts(n: number): Promise<ContactSearchEntry[]> {
        return dispatch(fetchTopNContactsFromCompanyAnalysisThunk(n));
    }

    return (
        <CRMContactsExportPanelContainer
            contacts={rows}
            totalCount={totalCount}
            isLoading={isLoading}
            countries={countries}
            onDeactivate={props.onDeactivate}
            onSuccess={refetchContacts}
            onExportToExcel={handleExcelExportSubmit}
            onFetchTopNContacts={fetchTopNContacts}
        />
    );
};

export default ActivatedExportPanelContainer;
